import { Link } from "react-router-dom";
import { CTASection } from "@/components/Shared";
import MeetShamima from "@/components/MeetShamima";
import SmartPathways from "@/components/SmartPathways";
import PricingHomeSection from "@/components/PricingHomeSection";
import FeaturedIn from "@/components/FeaturedIn";
import { GraduationCap, Briefcase, Users, Plane, Building2, AlertCircle, ArrowRight, CheckCircle2, Quote, Award, Sparkles, Calculator, ClipboardCheck } from "lucide-react";

const services = [
  { icon: Briefcase, title: "Skilled Migration", subclass: "189 · 190 · 491", to: "/services/skilled", desc: "EOI, SkillSelect, occupation list, skills assessment & points review.", fee: "from AUD 6,135" },
  { icon: Building2, title: "Employer Sponsored", subclass: "482 · 186 · 494", to: "/services/employer", desc: "Skills in Demand, ENS and regional employer sponsorship.", fee: "from AUD 4,015" },
  { icon: Users, title: "Partner Visa", subclass: "820/801 · 309/100", to: "/services/family", desc: "Onshore and offshore partner visas with strong evidence packs.", fee: "from AUD 11,710" },
  { icon: Users, title: "Parent Visa", subclass: "103 · 143 · 173 · 864", to: "/services/family", desc: "Contributory and non-contributory parent and aged-parent streams.", fee: "from AUD 6,300" },
  { icon: GraduationCap, title: "Graduate Visa", subclass: "Subclass 485", to: "/temporary-graduate-485", desc: "Eligibility review and post-study work / PR pathway planning.", fee: "from AUD 5,750" },
  { icon: Plane, title: "Visitor Visa", subclass: "Subclass 600", to: "/services/visitor", desc: "Tourist, family-sponsored visitor visas and invitation guidance.", fee: "from AUD 250" },
  { icon: Award, title: "Australian Citizenship", subclass: "Conferral · Descent", to: "/services/citizenship", desc: "Citizenship by conferral and by descent — eligibility, test, ceremony.", fee: "from AUD 575" },
];

export default function Home() {
  return (
    <div>
      {/* Hero — full-bleed image with overlay */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        {/* Full-bleed image */}
        <div className="relative h-[46vh] min-h-[420px] md:min-h-[400px] lg:min-h-[420px] lg:max-h-[480px] w-full">
          <img
            src="/assets/hero-au-nz.jpg"
            alt="Sydney Harbour and New Zealand's Milford Sound — Australia & New Zealand"
            className="absolute inset-0 w-full h-full object-cover object-[center_45%]"
          />
          {/* Even overlay for centred text legibility */}
          <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-[#1B1430]/70 via-[#29324A]/45 to-[#1B1430]/70" />

          {/* Content — centred */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl mx-auto text-center fade-up">
                <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold tracking-[0.22em] uppercase text-white/90 mb-4 px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20">
                  <span className="brand-dot bg-[#C43A89]" />
                  Australia &amp; New Zealand Immigration Specialist
                </div>

                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-white">
                  Turning Australian &amp; New Zealand Dreams into{" "}
                  <span className="text-[#F8B5D6]">Real Opportunities</span>{" "}
                  and Lasting Futures
                </h1>

                <p className="mt-5 text-sm sm:text-base text-white/85 leading-relaxed max-w-xl mx-auto">
                  We combine professional expertise, practical tools and genuine personal support to help students, skilled professionals, families and businesses navigate Australian and New Zealand pathways with confidence.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Hairline gradient under hero */}
        <span aria-hidden className="block h-px w-full bg-gradient-to-r from-transparent via-[#C43A89]/40 to-transparent" />
      </section>

      {/* Services section removed per request */}

      {/* Smart Pathways — four business funnels, immediately below hero */}
      <SmartPathways />

      {/* Assessment Hub — three tools, premium compact */}
      <section data-testid="assessment-hub" className="py-20 md:py-28 bg-[#F8F9FA] relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#29324A]/40 to-transparent" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-4 flex items-center gap-3">
              <span className="brand-dot bg-[#C43A89]" />
              Free Online Tools
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#29324A] leading-[1.05]">
              <span className="relative inline-block">
                <span className="relative z-10 text-[#29324A]">Migration Assessment Tools</span>
                <span className="absolute -bottom-1 left-0 right-0 h-2 bg-[#C43A89]/20 z-0" />
              </span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Whether you&apos;re looking to study, migrate, secure employer sponsorship or explore permanent residency pathways, our professional assessment tools provide personalised, expert-designed insights to help you understand your options and take the next step with confidence.
            </p>
            <p className="mt-3 text-[13px] font-medium text-[#29324A] tracking-tight">
              Built by Registered Migration Agents · trusted by students, skilled professionals and families worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-3 md:gap-4">
            {[
              {
                tag: "Assessment Tool",
                title: "Skilled Migration Pathway Assessment",
                desc: "Discover potential visa options, estimate your points, explore sponsorship opportunities and receive a personalised migration action plan.",
                to: "/skilled-migration-assessment",
                cta: "Start Free Assessment",
                tone: "pink",
                featured: true,
                span: "lg:col-span-3",
                testid: "tool-skilled-migration",
              },
              {
                tag: "Assessment Tool",
                title: "Parent Visa Pathway Assessment",
                desc: "Compare all 8 parent visa subclasses in minutes — our assessment engine runs the Balance of Family test, Aged Parent gate and ranks the best match for your family.",
                to: "/parent-visa-finder",
                cta: "Find Best Parent Visa",
                tone: "purple",
                featured: true,
                span: "lg:col-span-3",
                testid: "tool-parent-visa",
              },
              {
                tag: "Statement Builder",
                title: "GS & SOP Statement Builder",
                desc: "Draft your Genuine Student (GS) responses, SOP and supporting student visa statements with our guided, professionally structured statement builder.",
                to: "/visa-statement-builder",
                cta: "Start Now",
                tone: "pink",
                span: "lg:col-span-2",
                testid: "tool-gs-sop",
              },
              {
                tag: "Skilled Migration",
                title: "Points Calculator",
                desc: "Estimate your Australian skilled migration points based on age, English, work experience, education and other eligibility factors.",
                to: "/points-calculator",
                cta: "Calculate Points",
                tone: "purple",
                icon: Calculator,
                span: "lg:col-span-2",
                testid: "tool-points-calculator",
              },
              {
                tag: "Pathway Report",
                title: "PR Assessment",
                desc: "Complete a professional assessment to explore your Australian permanent residency pathway options.",
                to: "/pr-assessment",
                cta: "Start Assessment",
                tone: "navy",
                icon: ClipboardCheck,
                span: "lg:col-span-2",
                testid: "tool-pr-assessment",
              },
              {
                tag: "Finishing your studies? Start here",
                title: "485 Visa & Future Pathway Check",
                desc: "Assess your Temporary Graduate (485) visa eligibility and plan what comes after — all in one free personalised report:",
                bullets: [
                  "Check if you qualify for the 485 — and which stream fits you",
                  "See how long you can stay and what it will cost",
                  "Get a step-by-step action plan for your time on the visa",
                  "Spot risks early — before your visa time runs out",
                ],
                to: "/485-pathway-planner",
                cta: "Assess & Plan My Future",
                tone: "pink",
                featured: true,
                badge: "New Tool",
                span: "lg:col-span-6",
                testid: "tool-485-planner",
              },
            ].map((t, i) => {
              const palette = {
                purple: { bg: "bg-[#29324A]", text: "text-[#29324A]", accent: "bg-[#29324A]/10" },
                pink: { bg: "bg-[#C43A89]", text: "text-[#C43A89]", accent: "bg-[#C43A89]/10" },
                navy: { bg: "bg-[#29324A]", text: "text-[#29324A]", accent: "bg-[#29324A]/10" },
              }[t.tone];
              const featuredBg = t.tone === "purple"
                ? "bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A]"
                : "bg-gradient-to-br from-[#29324A] via-[#29324A] to-[#C43A89]";
              return (
                <Link
                  key={i}
                  to={t.to}
                  data-testid={t.testid}
                  className={`group relative transition-all duration-300 flex flex-col overflow-hidden ${t.span || ""} ${
                    t.featured
                      ? `${featuredBg} text-white border border-transparent shadow-xl shadow-[#29324A]/15 hover:-translate-y-1 hover:shadow-2xl ${t.badge ? "ring-2 ring-[#C43A89]/70 ring-offset-2" : ""}`
                      : "bg-white border border-slate-200 hover:border-transparent hover:shadow-xl hover:-translate-y-1"
                  }`}
                >
                  {t.badge && (
                    <span className="absolute top-0 right-0 z-10 bg-[#C43A89] text-white text-[9px] font-bold tracking-[0.2em] uppercase px-3 py-1.5">
                      {t.badge}
                    </span>
                  )}
                  {!t.featured && <span aria-hidden className={`absolute inset-x-0 top-0 h-1 ${palette.bg}`} />}
                  {t.featured && (
                    <span aria-hidden className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                  )}
                  <div className="relative p-7 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-6">
                      <div className={`inline-flex items-center justify-center h-10 w-10 ${t.featured ? "bg-white/15" : palette.accent}`}>
                        {t.featured ? (
                          <Sparkles className="h-5 w-5 text-white" />
                        ) : t.icon ? (
                          <t.icon className={`h-5 w-5 ${palette.text}`} />
                        ) : (
                          <span className={`font-display text-lg ${palette.text} leading-none`}>
                            {String(i + 1).padStart(2, "0")}
                          </span>
                        )}
                      </div>
                      <span className={`text-[10px] font-bold tracking-[0.18em] uppercase ${t.featured ? "text-[#C43A89]" : palette.text}`}>
                        {t.tag}
                      </span>
                    </div>
                    <h3 className={`font-display text-2xl leading-tight mb-3 ${t.featured ? "text-white" : "text-[#29324A]"}`}>{t.title}</h3>
                    <p className={`text-sm leading-relaxed ${t.bullets ? "mb-4" : "mb-8 flex-grow"} ${t.featured ? "text-white/85" : "text-slate-600"}`}>{t.desc}</p>
                    {t.bullets && (
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-8 flex-grow">
                        {t.bullets.map((b) => (
                          <div key={b} className="flex gap-2 text-[13px] text-white/90 leading-relaxed">
                            <CheckCircle2 className="h-4 w-4 text-[#C43A89] shrink-0 mt-0.5" /> {b}
                          </div>
                        ))}
                      </div>
                    )}
                    <span className={`inline-flex items-center justify-between text-xs font-bold tracking-wider uppercase ${t.featured ? "text-white" : palette.text} group-hover:gap-3 transition-all`}>
                      {t.cta}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 bg-white border-l-4 border-[#C43A89]">
            <CheckCircle2 className="h-5 w-5 text-[#29324A] shrink-0 mt-0.5 sm:mt-0" />
            <p className="text-sm text-slate-600 leading-relaxed">
              <span className="font-bold text-[#29324A]">Need personalised advice?</span> Book a confidential consultation (AUD 110) with an Registered Migration Agent — phone, Zoom or in-person across Sydney, Melbourne and Perth.
            </p>
            <Link
              to="/book-consultation"
              data-testid="assessment-hub-book-cta"
              className="shrink-0 inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-5 py-3 transition-colors btn-shimmer"
            >
              Book consultation <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Meet Shamima */}
      <MeetShamima />

      {/* Featured In media */}
      <FeaturedIn />

      {/* Pricing — transparent fees teaser + inquiry form */}
      <PricingHomeSection />

      {/* "Three steps to clarity" / How it works removed per request */}

      {/* Quote section removed per request */}

      {/* Who we support — section removed per request */}

      {/* Awards moved to /about page */}

      {/* Latest Updates moved to footer per request */}

      <CTASection />
    </div>
  );
}